import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreateInvoiceForm } from "@/components/invoice/CreateInvoiceForm";
import { UploadInvoiceForm } from "@/components/invoice/UploadInvoiceForm";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { showToast } from "@/lib/toast";

interface AddInvoiceDialogProps {
  trigger?: React.ReactNode;
}

export const AddInvoiceDialog = ({ trigger }: AddInvoiceDialogProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const handleSuccess = (action: "created" | "uploaded") => {
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["invoices-with-schedules"] });
    // showToast.success(`Invoice ${action} successfully`);
  };

  useEffect(() => {
    if (open) {
      const invoiceNumberInput = document.querySelector('input[name="invoiceNumber"]') as HTMLInputElement;
      if (invoiceNumberInput) {
        setTimeout(() => {
          invoiceNumberInput.focus();
        }, 100);
      }
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setTimeout(() => {
        const dialogContent = document.querySelector('[role="dialog"]');
        if (dialogContent) {
          dialogContent.scrollTop = 0;
        }
      }, 0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="w-full sm:w-auto hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200">
            <Plus className="h-4 w-4 mr-2" />
            <span className="whitespace-nowrap">{t("add_invoice_dialog.add_invoice", "Add Invoice")}</span>
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-[95vw] sm:max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6 bg-[#F1F6F5] border-none mx-2">
        <DialogHeader>
          <DialogTitle></DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="create" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#BCC4F4] h-auto">
            <TabsTrigger value="create" className="text-xs sm:text-sm py-2 flex items-center justify-center">{t("add_invoice_dialog.create_invoice", "Create Invoice")}</TabsTrigger>
            <TabsTrigger value="upload" disabled className="text-xs sm:text-sm py-2 flex items-center justify-center">{t("add_invoice_dialog.upload_invoice", "Upload Invoice (🔜)")}</TabsTrigger>
          </TabsList>
          <TabsContent value="create" className="mt-4 sm:mt-6">
            <CreateInvoiceForm mode="create" onSuccess={() => handleSuccess("created")} />
          </TabsContent>
          <TabsContent value="upload" className="mt-4 sm:mt-6">
            <UploadInvoiceForm onSuccess={() => handleSuccess("uploaded")} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
